import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../Component/Navbar'
import axiosInstance from '../Api/axiosInstance'

const enterpriseData = JSON.parse(localStorage.getItem("enterpriseData") || "{}")
const COMPANY_CODE = enterpriseData.companyCode
const API_BASE = 'https://api.imtransporter.com'

const initialForm = {
    employeCode: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailId: '',
    password: '',
    department: '',
    designation: '',
    requirements: '',
}

const OL = '#6b7c3e'
const OL_LIGHT = '#8a9e52'
const OL_PALE = '#f0f4e8'
const OL_HEADER = '#e8eedd'
const OL_DARK = '#3d4a22'

const navItems = [
    { label: 'Dashboard', icon: '📊', path: '/dashboard' },
    { label: 'Trip Details', icon: '🚚', path: '/trip-details' },
    { label: 'Saved Address', icon: '📍', path: '/save-address' },
    { label: 'Employee Details', icon: '👤', path: '/employee-request' },
    { label: 'Branch Details', icon: '🏢', path: '/branch-details' },
    { label: 'Account', icon: '📋', path: '/account' },
]

const statusStyle = {
    Pending: { color: '#b45309', bg: '#fef3c7' },
    Approved: { color: '#15803d', bg: '#dcfce7' },
    Rejected: { color: '#b91c1c', bg: '#fee2e2' },
}
const STATUS_MAP = {
    1: 'Pending',
    2: 'Approved',
    3: 'Rejected',
}

const EmployeeRequest = () => {
    const navigate = useNavigate()
    const [activeNav, setActiveNav] = useState('Employee Details')
    const [employees, setEmployees] = useState([])
    const [showModal, setShowModal] = useState(false)
    const [form, setForm] = useState(initialForm)
    const [loading, setLoading] = useState(false)
    const [listLoading, setListLoading] = useState(false)
    const [error, setError] = useState('')

    // ── Fetch employee requests on page load ──
    useEffect(() => {
        const fetchEmployees = async () => {
            setListLoading(true)
            try {
                const res = await axiosInstance.get(
                    `/employee/register/requests/get`,
                    {
                        params: { employeCompanyCode: COMPANY_CODE },
                        useAuth: true,
                    }
                )
                setEmployees(res.data?.data || res.data || [])
            } catch (err) {
                console.error('Failed to fetch employees:', err)
            } finally {
                setListLoading(false)
            }
        }

        fetchEmployees()
    }, [])

    const handleChange = (e) => {
        setError('')
        setForm({ ...form, [e.target.name]: e.target.value })
    }

    const handleSave = async () => {
        const required = ['employeCode', 'firstName', 'lastName', 'phoneNumber', 'emailId', 'password']
        if (required.some((k) => !form[k].trim())) {
            setError('Please fill all required fields.')
            return
        }

        const payload = {
            employeCode: form.employeCode,
            employeCompanyCode: COMPANY_CODE,
            firstName: form.firstName,
            lastName: form.lastName,
            emailId: form.emailId,
            password: form.password,
            phoneNumber: form.phoneNumber,
            deviceToken: 'web',
            requirements: form.requirements,
        }

        setLoading(true)
        setError('')

        try {
            await axiosInstance.post(
                `/employee/register/request/add`,
                payload,
                { useAuth: true }
            )

            // Push API-shaped object so table displays correctly
            setEmployees(prev => [...prev, {
                employeCode: form.employeCode,
                firstName: form.firstName,
                lastName: form.lastName,
                phoneNumber: form.phoneNumber,
                emailId: form.emailId,
                requirements: form.requirements,
                status: 'Pending',
            }])
            setForm(initialForm)
            setShowModal(false)
        } catch (err) {
            const message = err?.response?.data?.message || err.message || 'Failed to submit request.'
            setError(message)
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (i) => setEmployees(employees.filter((_, idx) => idx !== i))

    const handleCloseModal = () => {
        if (!loading) {
            setShowModal(false)
            setError('')
            setForm(initialForm)
        }
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: 'DM Sans', sans-serif; }
                .er-nav-btn:hover  { background: ${OL_PALE} !important; color: ${OL_DARK} !important; }
                .er-row:hover td   { background: #f4f7f0 !important; }
                .er-add-btn:hover  { background: ${OL_LIGHT} !important; }
                .er-edit-btn:hover { background: ${OL_PALE} !important; }
                .er-del-btn:hover  { background: #fee2e2 !important; }
                .er-cancel:hover   { background: #f3f4f6 !important; }
                .er-input:focus {
                    border-color: ${OL} !important;
                    box-shadow: 0 0 0 3px rgba(107,124,62,0.12) !important;
                    background: #fff !important;
                    outline: none;
                }
                .er-save-btn:hover:not(:disabled) { background: ${OL_LIGHT} !important; }
                .er-save-btn:disabled { opacity: 0.65; cursor: not-allowed; }
                .er-spinner {
                    display: inline-block;
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite;
                    margin-right: 6px;
                    vertical-align: middle;
                }
                .er-list-spinner {
                    display: inline-block;
                    width: 22px; height: 22px;
                    border: 3px solid rgba(107,124,62,0.2);
                    border-top-color: ${OL};
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>

            <Navbar />
            <div style={S.shell}>

                {/* ── Sidebar ── */}
                <aside style={S.sidebar}>
                    <div style={S.sidebarLogo}>
                        <span style={S.logoIcon}>🚛</span>
                        <span style={S.logoText}>Menu</span>
                    </div>
                    <nav style={S.nav}>
                        {navItems.map((item) => (
                            <button
                                key={item.label}
                                className="er-nav-btn"
                                style={{
                                    ...S.navItem,
                                    ...(activeNav === item.label ? S.navItemActive : {}),
                                }}
                                onClick={() => {
                                    setActiveNav(item.label)
                                    if (item.path) navigate(item.path)
                                }}
                            >
                                <span style={S.navIcon}>{item.icon}</span>
                                <span>{item.label}</span>
                                {activeNav === item.label && <span style={S.navIndicator} />}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* ── Main ── */}
                <main style={S.main}>
                    <div style={S.header}>
                        <div>
                            <h1 style={S.title}>Employee Request Details</h1>
                            <p style={S.subtitle}>Manage and track employee requests</p>
                        </div>
                        <button className="er-add-btn" style={S.addBtn} onClick={() => setShowModal(true)}>
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            Add Employee Request
                        </button>
                    </div>

                    {/* Table */}
                    <div style={S.tableWrapper}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    {['Emp. Code', 'Name', 'Phone', 'Email', 'Requirements', 'Status'].map((h) => (
                                        <th key={h} style={S.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {listLoading ? (
                                    <tr>
                                        <td colSpan={7} style={S.emptyCell}>
                                            <div style={S.emptyState}>
                                                <span className="er-list-spinner" />
                                                <p style={{ color: '#888' }}>Loading employees...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : employees.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={S.emptyCell}>
                                            <div style={S.emptyState}>
                                                <span style={{ fontSize: 38 }}>👤</span>
                                                <p>No employee requests found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    employees.map((emp, i) => {
                                        const st = statusStyle[emp.status] || statusStyle.Pending
                                        return (
                                            <tr key={i} className="er-row" style={S.tr}>
                                                <td style={S.td}>
                                                    <span style={S.empCodeBadge}>{emp.employeCode}</span>
                                                </td>
                                                <td style={{ ...S.td, fontWeight: 600, color: OL_DARK }}>
                                                    {emp.firstName} {emp.lastName}
                                                </td>
                                                <td style={{ ...S.td, letterSpacing: '0.02em' }}>{emp.phoneNumber}</td>
                                                <td style={S.td}>{emp.emailId}</td>
                                                <td style={{ ...S.td, maxWidth: 180 }}>{emp.requirements || '—'}</td>
                                                <td style={S.td}>
                                                    {(() => {
                                                        const label = STATUS_MAP[emp.status] || 'Pending'
                                                        const st = statusStyle[label]
                                                        return (
                                                            <span style={{
                                                                ...S.statusBadge,
                                                                color: st.color,
                                                                background: st.bg,
                                                                border: `1px solid ${st.color}40`,
                                                            }}>
                                                                {label}
                                                            </span>
                                                        )
                                                    })()}
                                                </td>

                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </main>
            </div>

            {/* ── Modal ── */}
            {showModal && (
                <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
                    <div style={S.modal}>
                        <div style={S.modalHeader}>
                            <span style={S.modalTitle}>Add Employee Request</span>
                            <button style={S.closeBtn} onClick={handleCloseModal} disabled={loading}>✕</button>
                        </div>

                        <div style={S.formGrid}>
                            {[
                                { label: 'EMP. CODE *', name: 'employeCode', placeholder: 'e.g. EMP001', type: 'text' },
                                { label: 'FIRST NAME *', name: 'firstName', placeholder: 'First name', type: 'text' },
                                { label: 'LAST NAME *', name: 'lastName', placeholder: 'Last name', type: 'text' },
                                { label: 'PHONE *', name: 'phoneNumber', placeholder: '+91 XXXXX XXXXX', type: 'tel' },
                                { label: 'EMAIL *', name: 'emailId', placeholder: 'employee@company.com', type: 'email' },
                                { label: 'PASSWORD *', name: 'password', placeholder: 'Min 8 characters', type: 'password' },
                                { label: 'REQUIREMENTS', name: 'requirements', placeholder: 'Any specific requirements', type: 'text', full: true },
                            ].map(({ label, name, placeholder, type, full }) => (
                                <div key={name} style={{ ...S.formGroup, gridColumn: full ? '1 / -1' : undefined }}>
                                    <label style={S.label}>{label}</label>
                                    <input
                                        className="er-input"
                                        style={S.input}
                                        type={type}
                                        name={name}
                                        value={form[name]}
                                        onChange={handleChange}
                                        placeholder={placeholder}
                                    />
                                </div>
                            ))}
                        </div>

                        {error && (
                            <div style={S.errorBox}>
                                <span>⚠️</span> {error}
                            </div>
                        )}

                        <div style={S.modalActions}>
                            <button className="er-cancel" style={S.cancelBtn} onClick={handleCloseModal} disabled={loading}>
                                Cancel
                            </button>
                            <button
                                className="er-save-btn"
                                style={S.saveBtn}
                                onClick={handleSave}
                                disabled={loading}
                            >
                                {loading && <span className="er-spinner" />}
                                {loading ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default EmployeeRequest

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
    shell: {
        display: 'flex',
        minHeight: '100vh',
        fontFamily: "'DM Sans', sans-serif",
        background: '#f7f8f4',
        color: '#2c2c2c',
    },
    sidebar: {
        width: 220,
        background: '#fff',
        borderRight: '1px solid #e0e5d4',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0',
        flexShrink: 0,
        boxShadow: '2px 0 12px rgba(107,124,62,0.07)',
    },
    sidebarLogo: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 20px 24px',
        borderBottom: '1px solid #e8eedd',
        marginBottom: 12,
    },
    logoIcon: { fontSize: 22 },
    logoText: { fontSize: 18, fontWeight: 700, color: OL_DARK, letterSpacing: '-0.3px' },
    nav: { display: 'flex', flexDirection: 'column', gap: 2, padding: '0 10px' },
    navItem: {
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
        borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer',
        fontSize: 14, fontWeight: 500, color: '#666', textAlign: 'left',
        position: 'relative', transition: 'all 0.18s ease', fontFamily: "'DM Sans', sans-serif",
    },
    navItemActive: { background: OL_PALE, color: OL_DARK, fontWeight: 600 },
    navIcon: { fontSize: 15 },
    navIndicator: {
        position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
        width: 3, height: 20, borderRadius: '0 3px 3px 0', background: OL,
    },
    main: { flex: 1, padding: '32px 36px', overflow: 'auto' },
    header: {
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: 28,
    },
    title: { fontSize: 26, fontWeight: 700, color: OL_DARK, letterSpacing: '-0.5px' },
    subtitle: { fontSize: 13, color: '#888', marginTop: 3 },
    addBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 7,
        background: OL, color: '#fff', border: 'none', borderRadius: 9,
        padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif",
        boxShadow: '0 2px 8px rgba(107,124,62,0.22)', transition: 'background 0.15s',
        whiteSpace: 'nowrap',
    },
    tableWrapper: {
        background: '#fff', borderRadius: 14, border: '1px solid #e2e8d4',
        overflow: 'hidden', boxShadow: '0 2px 16px rgba(107,124,62,0.07)',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: {
        background: OL_HEADER, color: OL_DARK, fontWeight: 600,
        padding: '13px 16px', textAlign: 'left', fontSize: 13,
        letterSpacing: '0.01em', borderBottom: `2px solid ${OL}30`,
        whiteSpace: 'nowrap',
    },
    tr: { borderBottom: '1px solid #eef1e8', transition: 'background 0.15s' },
    td: {
        padding: '13px 16px', color: '#3a3a3a', verticalAlign: 'middle',
        background: '#fff', transition: 'background 0.15s',
    },
    empCodeBadge: {
        display: 'inline-block', background: OL_PALE, color: OL_DARK,
        borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700,
        border: `1px solid ${OL}30`, fontFamily: 'monospace', letterSpacing: '0.04em',
    },
    statusBadge: {
        display: 'inline-block', padding: '4px 12px', borderRadius: 20,
        fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
    },
    actionBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
        borderRadius: 8, border: `1.5px solid ${OL}60`, background: '#fff',
        color: OL_DARK, fontWeight: 600, fontSize: 12, cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s ease',
    },
    emptyCell: { padding: '52px 16px' },
    emptyState: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 10, color: '#aaa', fontSize: 15,
    },
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.28)',
        backdropFilter: 'blur(2px)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
        background: '#fff', borderRadius: 18,
        boxShadow: '0 8px 48px rgba(0,0,0,0.16)',
        width: 560, maxWidth: '95vw', padding: '32px 32px 28px',
        maxHeight: '90vh', overflowY: 'auto',
    },
    modalHeader: {
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 22,
    },
    modalTitle: { fontSize: '1.1rem', fontWeight: 700, color: OL_DARK },
    closeBtn: {
        background: '#f3f4f6', border: 'none', borderRadius: 8,
        width: 32, height: 32, cursor: 'pointer', fontSize: '1rem',
        color: '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
    label: { fontSize: '0.75rem', fontWeight: 700, color: '#4b5563', letterSpacing: '0.04em' },
    input: {
        border: '1.5px solid #e0e5d4', borderRadius: 8, padding: '9px 12px',
        fontFamily: "'DM Sans', sans-serif", fontSize: '0.91rem', color: '#1f2937',
        background: OL_PALE, transition: 'border 0.13s, box-shadow 0.13s', width: '100%',
    },
    errorBox: {
        marginTop: 14, padding: '10px 14px', background: '#fff1f2',
        border: '1.5px solid #fca5a5', borderRadius: 8,
        color: '#b91c1c', fontSize: 13, fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 8,
    },
    modalActions: {
        display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22,
    },
    cancelBtn: {
        border: '1.5px solid #e5e7eb', background: '#fff', color: '#4b5563',
        borderRadius: 8, padding: '9px 20px', fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.91rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.13s',
    },
    saveBtn: {
        background: OL, color: '#fff', border: 'none', borderRadius: 8,
        padding: '9px 22px', fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.91rem', fontWeight: 600, cursor: 'pointer',
        transition: 'background 0.15s', display: 'inline-flex', alignItems: 'center',
    },
}